import React, { useState } from 'react';
import { ArrowLeft, Save, Plus, MapPin, FileText, CreditCard, Mail, Receipt, X, Check, Lock, Users, ChevronDown, ChevronUp, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { adminCreateCandidate, adminUpdateCandidateNotes, getAccompaniment, updateAccompaniment, getUserPurchasedPrograms, deleteCandidate, adminGetCandidatePayments, adminCreatePayment, adminUpdatePayment, adminDeletePayment, adminSendReminder, adminSendInvoice, adminGetCandidates, adminSyncCandidatesFromBookings, adminResetPassword, adminGrantAccess } from '../../services/api';

const AdminCandidatesTab = ({
  candidates, setCandidates, programs, token, loadData,
  selectedCandidate, setSelectedCandidate,
  candidateAccompaniment, setCandidateAccompaniment,
  candidatePurchases, setCandidatePurchases,
  candidatePayments, setCandidatePayments,
  showPaymentForm, setShowPaymentForm,
  paymentForm, setPaymentForm,
  savingAccompaniment, setSavingAccompaniment,
  showCreateCandidate, setShowCreateCandidate,
  creatingCandidate, setCreatingCandidate,
  newCandidate, setNewCandidate,
  candidateNotes, setCandidateNotes,
  savingNotes, setSavingNotes
}) => {
  const [syncing, setSyncing] = useState(false);

  const handleSyncFromBookings = async () => {
    setSyncing(true);
    try {
      const result = await adminSyncCandidatesFromBookings(token);
      if (result.created > 0) {
        toast.success(`${result.created} candidat(s) créé(s) depuis les réservations`);
        if (result.candidates) {
          result.candidates.forEach(c => {
            toast.info(`${c.name} — ${c.email} (mot de passe: ${c.password})`, { duration: 15000 });
          });
        }
        loadData();
      } else {
        toast.info('Tous les candidats ayant réservé ont déjà un compte.');
      }
    } catch (err) {
      toast.error(err.message);
    }
    setSyncing(false);
  };

  return (
          <div data-testid="admin-candidates">
            {selectedCandidate ? (
              /* CANDIDATE DETAIL VIEW */
              <div>
                <button onClick={() => { setSelectedCandidate(null); setCandidateAccompaniment(null); setCandidatePurchases([]); setCandidatePayments([]); setShowPaymentForm(false); }} className="btn-secondary" style={{ marginBottom: '1.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  <ArrowLeft size={16} /> Retour à la liste
                </button>
                <div className="card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#f3e8ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#7c3aed', fontSize: '1.1rem' }}>
                      {(selectedCandidate.name || selectedCandidate.email || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <h2 style={{ fontWeight: 700, fontSize: '1.1rem' }}>{selectedCandidate.name || '\u2014'}</h2>
                      <p className="body-small" style={{ color: 'var(--text-muted)' }}>{selectedCandidate.email}</p>
                      {selectedCandidate.phone && <p className="body-small" style={{ color: '#E75480', fontWeight: 600 }}>{selectedCandidate.phone}</p>}
                      {selectedCandidate.address && <p className="body-small" style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><MapPin size={13} /> {selectedCandidate.address}</p>}
                    </div>
                  </div>
                  {candidatePurchases.length > 0 && (
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {candidatePurchases.map((p, i) => (
                        <span key={i} style={{ padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.78rem', fontWeight: 600, background: p.formula === 'gold' ? '#FEF3C7' : p.formula === 'silver' ? '#F3F4F6' : p.kitId ? '#DBEAFE' : '#FDE8E8', color: p.formula === 'gold' ? '#92400E' : p.formula === 'silver' ? '#374151' : p.kitId ? '#1E40AF' : '#9B2C2C' }}>
                          {p.formula ? p.formula.toUpperCase() : `Kit ${p.kitId}`}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Identifiants de connexion */}
                  <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#FEF3C7', borderRadius: '8px', border: '1px solid #FCD34D' }} data-testid="candidate-credentials">
                    <p style={{ fontSize: '0.78rem', fontWeight: 700, color: '#92400E', marginBottom: '0.35rem' }}>Identifiants de connexion</p>
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.8rem', color: '#78350F' }}><strong>Email :</strong> {selectedCandidate.email}</span>
                      <span style={{ fontSize: '0.8rem', color: '#78350F' }}><strong>Mot de passe :</strong> {selectedCandidate.plainPassword || '(non disponible)'}</span>
                    </div>
                  </div>
                </div>

                {/* Notes de suivi */}
                <div className="card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }} data-testid="candidate-notes-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e3a5f', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FileText size={18} /> Notes de suivi
                    </h3>
                    <button
                      className="btn-primary"
                      disabled={savingNotes}
                      data-testid="save-candidate-notes-btn"
                      onClick={async () => {
                        setSavingNotes(true);
                        try {
                          await adminUpdateCandidateNotes(token, selectedCandidate.id, candidateNotes);
                          toast.success('Notes sauvegard\u00e9es !');
                        } catch (e) {
                          toast.error('Erreur lors de la sauvegarde');
                        }
                        setSavingNotes(false);
                      }}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', padding: '0.4rem 0.8rem' }}
                    >
                      <Save size={14} /> {savingNotes ? 'Sauvegarde...' : 'Sauvegarder'}
                    </button>
                  </div>
                  <textarea
                    value={candidateNotes}
                    onChange={e => setCandidateNotes(e.target.value)}
                    data-testid="candidate-notes-textarea"
                    placeholder="Ajoutez vos notes de suivi ici... (RDV, observations, actions \u00e0 faire, etc.)"
                    style={{
                      width: '100%', minHeight: '120px', padding: '0.75rem', border: '1px solid #d1d5db',
                      borderRadius: '8px', fontSize: '0.85rem', resize: 'vertical', outline: 'none',
                      fontFamily: 'inherit', lineHeight: '1.5', transition: 'border-color 0.2s'
                    }}
                    onFocus={e => e.target.style.borderColor = '#667eea'}
                    onBlur={e => e.target.style.borderColor = '#d1d5db'}
                  />
                  {selectedCandidate.adminNotes_updated_at && (
                    <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '0.4rem', fontSize: '0.72rem' }}>
                      Derni\u00e8re modification : {new Date(selectedCandidate.adminNotes_updated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>

                {/* Paiements & Échéancier */}
                <div className="card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }} data-testid="admin-payments-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e3a5f', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <CreditCard size={18} /> Paiements & Échéancier
                    </h3>
                    <button
                      className="btn-primary"
                      data-testid="add-payment-btn"
                      onClick={() => setShowPaymentForm(!showPaymentForm)}
                      style={{ fontSize: '0.8rem', padding: '0.35rem 0.7rem' }}
                    >
                      <Plus size={14} /> Ajouter un paiement
                    </button>
                  </div>

                  {showPaymentForm && (
                    <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Programme</label>
                          <select
                            data-testid="payment-program-select"
                            value={paymentForm.programId}
                            onChange={e => setPaymentForm(f => ({...f, programId: e.target.value}))}
                            style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem' }}
                          >
                            <option value="">-- Sélectionner --</option>
                            <option value="deap">DEAP</option>
                            <option value="deeje">DEEJE</option>
                            <option value="bac-assp">BAC ASSP</option>
                            <option value="cap-aepe">CAP AEPE</option>
                            <option value="oral-vae">Oral VAE</option>
                            <option value="livret-1-asp">Livret 1</option>
                            <option value="veille-pro">Veille Pro</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Formule</label>
                          <input
                            data-testid="payment-formula-input"
                            value={paymentForm.formula}
                            onChange={e => setPaymentForm(f => ({...f, formula: e.target.value}))}
                            placeholder="Gold, Silver, Bronze, Livret 1..."
                            style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Montant total (€)</label>
                          <input
                            data-testid="payment-amount-input"
                            type="number"
                            value={paymentForm.amount}
                            onChange={e => setPaymentForm(f => ({...f, amount: parseFloat(e.target.value) || 0}))}
                            style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Moyen de paiement</label>
                          <select
                            data-testid="payment-method-select"
                            value={paymentForm.paymentMethod}
                            onChange={e => setPaymentForm(f => ({...f, paymentMethod: e.target.value}))}
                            style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem' }}
                          >
                            <option value="virement">Virement bancaire</option>
                            <option value="stripe">Carte (Stripe)</option>
                            <option value="especes">Espèces</option>
                            <option value="cheque">Chèque</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Nombre de versements</label>
                          <select
                            data-testid="payment-nb-installments"
                            value={paymentForm.nbInstallments}
                            onChange={e => setPaymentForm(f => ({...f, nbInstallments: parseInt(e.target.value)}))}
                            style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem' }}
                          >
                            <option value={1}>1x (paiement unique)</option>
                            <option value={2}>2x</option>
                            <option value={3}>3x</option>
                            <option value={4}>4x</option>
                            <option value={5}>5x</option>
                            <option value={6}>6x</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Date 1er versement</label>
                          <input
                            data-testid="payment-first-date"
                            type="date"
                            value={paymentForm.firstDate}
                            onChange={e => setPaymentForm(f => ({...f, firstDate: e.target.value}))}
                            style={{ width: '100%', padding: '0.4rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem' }}
                          />
                        </div>
                      </div>
                      <button
                        data-testid="submit-payment-btn"
                        className="btn-primary"
                        style={{ fontSize: '0.82rem' }}
                        onClick={async () => {
                          if (!paymentForm.programId || !paymentForm.amount || !paymentForm.firstDate) {
                            toast.error('Remplissez programme, montant et date');
                            return;
                          }
                          const perInst = Math.floor(paymentForm.amount / paymentForm.nbInstallments);
                          const remainder = paymentForm.amount - perInst * paymentForm.nbInstallments;
                          const installments = [];
                          const startDate = new Date(paymentForm.firstDate);
                          for (let i = 0; i < paymentForm.nbInstallments; i++) {
                            const d = new Date(startDate);
                            d.setMonth(d.getMonth() + i);
                            installments.push({
                              amount: i === 0 ? perInst + remainder : perInst,
                              date: d.toISOString().split('T')[0],
                              status: i === 0 ? 'paid' : 'pending',
                              method: paymentForm.paymentMethod
                            });
                          }
                          try {
                            await adminCreatePayment(selectedCandidate.id, {
                              programId: paymentForm.programId,
                              formula: paymentForm.formula,
                              programName: paymentForm.formula + ' - ' + paymentForm.programId.toUpperCase(),
                              amount: paymentForm.amount,
                              paymentMethod: paymentForm.paymentMethod,
                              installments
                            }, token);
                            const updated = await adminGetCandidatePayments(selectedCandidate.id, token);
                            setCandidatePayments(updated);
                            setShowPaymentForm(false);
                            setPaymentForm({ programId: '', formula: '', programName: '', amount: 0, paymentMethod: 'virement', nbInstallments: 3, firstDate: '', installmentDates: [] });
                            toast.success('Paiement créé avec échéancier');
                          } catch (e) {
                            toast.error('Erreur lors de la création');
                          }
                        }}
                      >
                        Créer le paiement avec échéancier
                      </button>
                    </div>
                  )}

                  {candidatePayments.length === 0 && !showPaymentForm && (
                    <p className="body-small" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Aucun paiement enregistré</p>
                  )}

                  {candidatePayments.map((payment, pIdx) => (
                    <div key={pIdx} style={{ padding: '0.75rem', marginBottom: '0.75rem', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff' }} data-testid={`admin-payment-${pIdx}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{payment.formula || payment.metadata?.formula || '—'}</span>
                          <span style={{ padding: '0.15rem 0.5rem', borderRadius: '1rem', fontSize: '0.65rem', fontWeight: 600, background: '#e0e7ff', color: '#3730a3' }}>
                            {payment.paymentMethod === 'virement' ? 'Virement' : payment.paymentMethod === 'stripe' ? 'Stripe' : payment.paymentMethod || 'N/A'}
                          </span>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {payment.amountPaid || 0}€ / {payment.amount || 0}€
                          </span>
                        </div>
                        <button
                          data-testid={`delete-admin-payment-${pIdx}`}
                          onClick={async () => {
                            if (window.confirm('Supprimer ce paiement ?')) {
                              try {
                                await adminDeletePayment(payment.sessionId, token);
                                setCandidatePayments(prev => prev.filter(p => p.sessionId !== payment.sessionId));
                                toast.success('Paiement supprimé');
                              } catch (e) { toast.error('Erreur'); }
                            }
                          }}
                          style={{ padding: '0.15rem 0.4rem', borderRadius: '4px', fontSize: '0.65rem', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', cursor: 'pointer' }}
                        >
                          Supprimer
                        </button>
                      </div>
                      {payment.installments && payment.installments.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                          {payment.installments.map((inst, i) => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.3rem 0.5rem', borderRadius: '4px', background: inst.status === 'paid' ? '#f0fdf4' : '#fffbeb' }}>
                              <span style={{ fontSize: '0.78rem' }}>Versement {i + 1} — {new Date(inst.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} — {inst.amount}€</span>
                              <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                                {inst.status !== 'paid' && (
                                  <button
                                    data-testid={`send-reminder-${pIdx}-${i}`}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await adminSendReminder(payment.sessionId, i, token);
                                        toast.success('Rappel envoyé par email');
                                      } catch (err) { toast.error('Erreur envoi rappel'); }
                                    }}
                                    style={{
                                      padding: '0.15rem 0.4rem', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 600, cursor: 'pointer',
                                      background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: '3px'
                                    }}
                                    title="Envoyer un rappel par email"
                                  >
                                    <Mail size={10} /> Rappel
                                  </button>
                                )}
                                <button
                                  data-testid={`toggle-installment-${pIdx}-${i}`}
                                  onClick={async () => {
                                    const updatedInst = [...payment.installments];
                                    updatedInst[i] = { ...updatedInst[i], status: updatedInst[i].status === 'paid' ? 'pending' : 'paid' };
                                    try {
                                      await adminUpdatePayment(payment.sessionId, { installments: updatedInst }, token);
                                      const updated = await adminGetCandidatePayments(selectedCandidate.id, token);
                                      setCandidatePayments(updated);
                                      toast.success(updatedInst[i].status === 'paid' ? 'Versement marqué payé' : 'Versement marqué en attente');
                                    } catch (e) { toast.error('Erreur'); }
                                  }}
                                  style={{
                                    padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
                                    background: inst.status === 'paid' ? '#dcfce7' : '#fef3c7',
                                    color: inst.status === 'paid' ? '#166534' : '#92400e',
                                    border: `1px solid ${inst.status === 'paid' ? '#bbf7d0' : '#fcd34d'}`
                                  }}
                                >
                                  {inst.status === 'paid' ? 'Payé' : 'Marquer payé'}
                                </button>
                              </div>
                            </div>
                          ))}
                          <div style={{ marginTop: '0.25rem', background: '#e5e7eb', borderRadius: '1rem', height: '5px', overflow: 'hidden' }}>
                            <div style={{ width: `${payment.amount > 0 ? Math.round(((payment.amountPaid || 0) / payment.amount) * 100) : 0}%`, background: 'linear-gradient(90deg, #22c55e, #16a34a)', height: '100%', borderRadius: '1rem' }} />
                          </div>
                          {/* Bouton facture — visible quand tous les versements sont payés */}
                          {payment.installments.every(inst => inst.status === 'paid') && (
                            <button
                              data-testid={`send-invoice-${pIdx}`}
                              onClick={async () => {
                                try {
                                  const result = await adminSendInvoice(payment.sessionId, token);
                                  toast.success(result.message || 'Facture envoyée');
                                } catch (err) { toast.error('Erreur envoi facture'); }
                              }}
                              style={{
                                marginTop: '0.4rem', padding: '0.4rem 0.75rem', borderRadius: '6px', fontSize: '0.78rem',
                                fontWeight: 600, cursor: 'pointer', background: '#1e3a5f', color: 'white', border: 'none',
                                display: 'flex', alignItems: 'center', gap: '6px', alignSelf: 'flex-end'
                              }}
                            >
                              <Receipt size={14} /> Envoyer la facture
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Accompaniment Editing */}
                {candidateAccompaniment ? (
                  <div>
                    {(() => {
                      const kitPurchase = candidatePurchases.find(p => p.kitId);
                      const kitNumber = kitPurchase ? parseInt(kitPurchase.kitId.replace('kit-bloc-', '')) : null;
                      const isKit = !!kitNumber;
                      const isGold = candidatePurchases.some(p => p.formula === 'gold');
                      const isSilver = candidatePurchases.some(p => p.formula === 'silver');
                      const isOralVae = candidatePurchases.some(p => p.programId === 'oral-vae') || candidatePayments.some(p => p.programId === 'oral-vae');
                      const isLivret1 = candidatePurchases.some(p => p.programId === 'livret-1-asp') || candidatePayments.some(p => p.programId === 'livret-1-asp') || candidatePayments.some(p => (p.formula || '').toLowerCase().includes('livret 1'));
                      const showCorrections = !isOralVae && !isLivret1;
                      const filteredCorrections = isKit
                        ? (candidateAccompaniment.corrections || []).filter(c => c.number === kitNumber)
                        : (candidateAccompaniment.corrections || []);
                      return (<>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                      <h3 className="heading-3">Suivi de l'accompagnement</h3>
                      <button
                        className="btn-primary"
                        disabled={savingAccompaniment}
                        data-testid="save-accompaniment-btn"
                        onClick={async () => {
                          setSavingAccompaniment(true);
                          try {
                            await updateAccompaniment(selectedCandidate.id, candidateAccompaniment.programId, candidateAccompaniment);
                            toast.success('Suivi mis à jour avec succès !');
                          } catch (e) {
                            toast.error('Erreur lors de la sauvegarde');
                          }
                          setSavingAccompaniment(false);
                        }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                      >
                        <Save size={16} /> {savingAccompaniment ? 'Sauvegarde...' : 'Sauvegarder'}
                      </button>
                    </div>

                    {/* Corrections - Hidden for Oral VAE & Livret 1 */}
                    {showCorrections && (
                    <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                      <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem', color: '#1e3a5f' }}>Corrections Livret 2{isKit ? ` — Bloc ${kitNumber}` : ''}</h4>
                      <div style={{ display: 'grid', gap: '0.5rem' }}>
                        {filteredCorrections.map((c) => {
                          const idx = (candidateAccompaniment.corrections || []).findIndex(x => x.number === c.number);
                          return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '8px', background: c.status === 'corrige' ? '#f0fdf4' : c.status === 'en_cours' ? '#FFF7ED' : c.status === 'depose' ? '#EFF6FF' : '#f8fafc', border: `1px solid ${c.status === 'corrige' ? '#bbf7d0' : c.status === 'en_cours' ? '#fed7aa' : c.status === 'depose' ? '#bfdbfe' : '#e2e8f0'}`, flexWrap: 'wrap' }} data-testid={`admin-correction-${idx}`}>
                            <span style={{ fontWeight: 600, fontSize: '0.82rem', flex: 1, minWidth: '180px' }}>Bloc {c.number}: {c.label}</span>
                            <select
                              value={c.status || 'a_planifier'}
                              onChange={(e) => {
                                const updated = { ...candidateAccompaniment };
                                updated.corrections[idx].status = e.target.value;
                                if (e.target.value === 'corrige' && !c.date) {
                                  updated.corrections[idx].date = new Date().toISOString().split('T')[0];
                                }
                                setCandidateAccompaniment({ ...updated });
                              }}
                              style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white', fontWeight: 600, color: c.status === 'corrige' ? '#059669' : c.status === 'en_cours' ? '#D97706' : c.status === 'depose' ? '#2563EB' : '#6B7280' }}
                            >
                              <option value="a_planifier">— Non déposé</option>
                              <option value="depose">Déposé</option>
                              <option value="en_cours">En cours de correction</option>
                              <option value="corrige">Corrigé</option>
                            </select>
                            <input
                              type="date"
                              value={c.date || ''}
                              onChange={(e) => {
                                const updated = { ...candidateAccompaniment };
                                updated.corrections[idx].date = e.target.value || null;
                                setCandidateAccompaniment({ ...updated });
                              }}
                              style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white' }}
                            />
                          </div>
                          );
                        })}
                      </div>
                    </div>
                    )}

                    {/* Oral VAE - Espace Livret 2 */}
                    {isOralVae && (
                    <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                      <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.75rem', color: '#7c3aed' }}>Espace Livret 2 (lecture)</h4>
                      <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                        Formule Oral VAE — lecture complète du livret 2 incluse, pas de correction.
                      </p>
                    </div>
                    )}

                    {/* Livret 1 - Suivi recevabilité */}
                    {isLivret1 && (
                    <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                      <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.75rem', color: '#059669' }}>Suivi Livret 1 — Recevabilité</h4>
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Statut du Livret 1</label>
                          <select
                            data-testid="livret1-status-select"
                            value={candidateAccompaniment.livret1Status || 'en_cours'}
                            onChange={(e) => {
                              setCandidateAccompaniment({ ...candidateAccompaniment, livret1Status: e.target.value });
                            }}
                            style={{ padding: '0.4rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem', fontWeight: 600 }}
                          >
                            <option value="en_cours">En cours de rédaction</option>
                            <option value="deposited">Livret 1 déposé</option>
                            <option value="attente_recevabilite">En attente de recevabilité</option>
                            <option value="recevable">Recevable</option>
                            <option value="non_recevable">Non recevable</option>
                          </select>
                        </div>
                      </div>
                      <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: '0.5rem', fontStyle: 'italic' }}>
                        Accompagnement Livret 1 uniquement — pas de correction Livret 2.
                      </p>
                    </div>
                    )}

                    {/* Présentation personnelle (Gold/Silver only, not Kit, not Livret1) */}
                    {!isKit && !isLivret1 && (candidateAccompaniment.presentations_perso || []).length > 0 && (
                      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                        <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem', color: '#8B5CF6' }}>Présentation personnelle</h4>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {candidateAccompaniment.presentations_perso.map((pp, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.5rem 0.75rem', borderRadius: '8px', background: pp.date ? '#F5F3FF' : '#f8fafc', border: `1px solid ${pp.date ? '#c4b5fd' : '#e2e8f0'}` }} data-testid={`admin-presentation-${idx}`}>
                              <span style={{ fontWeight: 600, fontSize: '0.82rem', flex: 1 }}>{pp.label}</span>
                              <input
                                type="date"
                                value={pp.date || ''}
                                onChange={(e) => {
                                  const updated = { ...candidateAccompaniment };
                                  updated.presentations_perso[idx].date = e.target.value || null;
                                  setCandidateAccompaniment({ ...updated });
                                }}
                                style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem', background: 'white' }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Oraux blancs (Gold/Silver only, not Kit, not Livret1) */}
                    {!isKit && !isLivret1 && (candidateAccompaniment.oral_blancs || []).length > 0 && (
                      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                        <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem', color: '#F97316' }}>Oraux blancs</h4>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {candidateAccompaniment.oral_blancs.map((ob, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.5rem 0.75rem', borderRadius: '8px', background: ob.date ? '#FFF7ED' : '#f8fafc', border: `1px solid ${ob.date ? '#fed7aa' : '#e2e8f0'}` }} data-testid={`admin-oral-${idx}`}>
                              <span style={{ fontWeight: 600, fontSize: '0.82rem', flex: 1 }}>{ob.label}</span>
                              <input
                                type="date"
                                value={ob.date || ''}
                                onChange={(e) => {
                                  const updated = { ...candidateAccompaniment };
                                  updated.oral_blancs[idx].date = e.target.value || null;
                                  setCandidateAccompaniment({ ...updated });
                                }}
                                style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem', background: 'white' }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Oral blanc simple (Silver, not Livret1) */}
                    {!isKit && !isLivret1 && candidateAccompaniment.oral_blanc && !(candidateAccompaniment.oral_blancs || []).length && (
                      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                        <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem', color: '#F97316' }}>Oral blanc</h4>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.5rem 0.75rem', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                          <span style={{ fontWeight: 600, fontSize: '0.82rem', flex: 1 }}>Oral blanc de préparation</span>
                          <input
                            type="date"
                            value={candidateAccompaniment.oral_blanc?.date || ''}
                            onChange={(e) => {
                              const updated = { ...candidateAccompaniment, oral_blanc: { ...candidateAccompaniment.oral_blanc, date: e.target.value || null } };
                              setCandidateAccompaniment(updated);
                            }}
                            style={{ padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem', background: 'white' }}
                          />
                        </div>
                      </div>
                    )}

                    {/* TP IEPE: Appels explicatifs */}
                    {(candidateAccompaniment.appels || []).length > 0 && (
                      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                        <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem', color: '#10B981' }}>Appels explicatifs</h4>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {candidateAccompaniment.appels.map((a, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '8px', background: a.date ? '#ECFDF5' : '#f8fafc', border: `1px solid ${a.date ? '#a7f3d0' : '#e2e8f0'}`, flexWrap: 'wrap' }} data-testid={`admin-appel-${idx}`}>
                              <span style={{ fontWeight: 600, fontSize: '0.82rem', flex: 1, minWidth: '200px' }}>{a.label}</span>
                              <select
                                value={a.status || 'a_planifier'}
                                onChange={(e) => {
                                  const updated = { ...candidateAccompaniment };
                                  updated.appels[idx].status = e.target.value;
                                  if (e.target.value === 'fait' && !a.date) {
                                    updated.appels[idx].date = new Date().toISOString().split('T')[0];
                                  }
                                  setCandidateAccompaniment({ ...updated });
                                }}
                                style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white', fontWeight: 600, color: a.status === 'fait' ? '#059669' : a.status === 'planifie' ? '#2563EB' : '#6B7280' }}
                              >
                                <option value="a_planifier">À planifier</option>
                                <option value="planifie">Planifié</option>
                                <option value="fait">Fait</option>
                              </select>
                              <input type="date" value={a.date || ''} onChange={(e) => { const updated = { ...candidateAccompaniment }; updated.appels[idx].date = e.target.value || null; setCandidateAccompaniment({ ...updated }); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white' }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* TP IEPE: RDV Personnalisation diaporama */}
                    {(candidateAccompaniment.rdv_diaporama || []).length > 0 && (
                      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                        <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem', color: '#F97316' }}>RDV Personnalisation diaporama</h4>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {candidateAccompaniment.rdv_diaporama.map((r, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '8px', background: r.date ? '#FFF7ED' : '#f8fafc', border: `1px solid ${r.date ? '#fed7aa' : '#e2e8f0'}`, flexWrap: 'wrap' }} data-testid={`admin-rdv-diapo-${idx}`}>
                              <span style={{ fontWeight: 600, fontSize: '0.82rem', flex: 1, minWidth: '200px' }}>{r.label}</span>
                              <select value={r.status || 'a_planifier'} onChange={(e) => { const updated = { ...candidateAccompaniment }; updated.rdv_diaporama[idx].status = e.target.value; if (e.target.value === 'fait' && !r.date) updated.rdv_diaporama[idx].date = new Date().toISOString().split('T')[0]; setCandidateAccompaniment({ ...updated }); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white', fontWeight: 600, color: r.status === 'fait' ? '#059669' : r.status === 'planifie' ? '#2563EB' : '#6B7280' }}>
                                <option value="a_planifier">À planifier</option>
                                <option value="planifie">Planifié</option>
                                <option value="fait">Fait</option>
                              </select>
                              <input type="date" value={r.date || ''} onChange={(e) => { const updated = { ...candidateAccompaniment }; updated.rdv_diaporama[idx].date = e.target.value || null; setCandidateAccompaniment({ ...updated }); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white' }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* TP IEPE: Entretiens techniques blancs */}
                    {(candidateAccompaniment.entretiens_techniques || []).length > 0 && (
                      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                        <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem', color: '#8B5CF6' }}>Entretiens techniques blancs</h4>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {candidateAccompaniment.entretiens_techniques.map((et, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '8px', background: et.date ? '#F5F3FF' : '#f8fafc', border: `1px solid ${et.date ? '#c4b5fd' : '#e2e8f0'}`, flexWrap: 'wrap' }} data-testid={`admin-technique-${idx}`}>
                              <span style={{ fontWeight: 600, fontSize: '0.82rem', flex: 1, minWidth: '200px' }}>{et.label}</span>
                              <select value={et.status || 'a_planifier'} onChange={(e) => { const updated = { ...candidateAccompaniment }; updated.entretiens_techniques[idx].status = e.target.value; if (e.target.value === 'fait' && !et.date) updated.entretiens_techniques[idx].date = new Date().toISOString().split('T')[0]; setCandidateAccompaniment({ ...updated }); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white', fontWeight: 600, color: et.status === 'fait' ? '#059669' : et.status === 'planifie' ? '#2563EB' : '#6B7280' }}>
                                <option value="a_planifier">À planifier</option>
                                <option value="planifie">Planifié</option>
                                <option value="fait">Fait</option>
                              </select>
                              <input type="date" value={et.date || ''} onChange={(e) => { const updated = { ...candidateAccompaniment }; updated.entretiens_techniques[idx].date = e.target.value || null; setCandidateAccompaniment({ ...updated }); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white' }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* TP IEPE: Entretiens diaporama blancs */}
                    {(candidateAccompaniment.entretiens_diaporama || []).length > 0 && (
                      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                        <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem', color: '#06B6D4' }}>Entretiens diaporama blancs</h4>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {candidateAccompaniment.entretiens_diaporama.map((ed, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '8px', background: ed.date ? '#ECFEFF' : '#f8fafc', border: `1px solid ${ed.date ? '#a5f3fc' : '#e2e8f0'}`, flexWrap: 'wrap' }} data-testid={`admin-diapo-blanc-${idx}`}>
                              <span style={{ fontWeight: 600, fontSize: '0.82rem', flex: 1, minWidth: '200px' }}>{ed.label}</span>
                              <select value={ed.status || 'a_planifier'} onChange={(e) => { const updated = { ...candidateAccompaniment }; updated.entretiens_diaporama[idx].status = e.target.value; if (e.target.value === 'fait' && !ed.date) updated.entretiens_diaporama[idx].date = new Date().toISOString().split('T')[0]; setCandidateAccompaniment({ ...updated }); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white', fontWeight: 600, color: ed.status === 'fait' ? '#059669' : ed.status === 'planifie' ? '#2563EB' : '#6B7280' }}>
                                <option value="a_planifier">À planifier</option>
                                <option value="planifie">Planifié</option>
                                <option value="fait">Fait</option>
                              </select>
                              <input type="date" value={ed.date || ''} onChange={(e) => { const updated = { ...candidateAccompaniment }; updated.entretiens_diaporama[idx].date = e.target.value || null; setCandidateAccompaniment({ ...updated }); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white' }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* TP IEPE: Entretiens finaux blancs */}
                    {(candidateAccompaniment.entretiens_finaux || []).length > 0 && (
                      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
                        <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '1rem', color: '#10B981' }}>Entretiens finaux blancs</h4>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {candidateAccompaniment.entretiens_finaux.map((ef, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: '8px', background: ef.date ? '#ECFDF5' : '#f8fafc', border: `1px solid ${ef.date ? '#a7f3d0' : '#e2e8f0'}`, flexWrap: 'wrap' }} data-testid={`admin-final-${idx}`}>
                              <span style={{ fontWeight: 600, fontSize: '0.82rem', flex: 1, minWidth: '200px' }}>{ef.label}</span>
                              <select value={ef.status || 'a_planifier'} onChange={(e) => { const updated = { ...candidateAccompaniment }; updated.entretiens_finaux[idx].status = e.target.value; if (e.target.value === 'fait' && !ef.date) updated.entretiens_finaux[idx].date = new Date().toISOString().split('T')[0]; setCandidateAccompaniment({ ...updated }); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white', fontWeight: 600, color: ef.status === 'fait' ? '#059669' : ef.status === 'planifie' ? '#2563EB' : '#6B7280' }}>
                                <option value="a_planifier">À planifier</option>
                                <option value="planifie">Planifié</option>
                                <option value="fait">Fait</option>
                              </select>
                              <input type="date" value={ef.date || ''} onChange={(e) => { const updated = { ...candidateAccompaniment }; updated.entretiens_finaux[idx].date = e.target.value || null; setCandidateAccompaniment({ ...updated }); }} style={{ padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.78rem', background: 'white' }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>);
                    })()}
                  </div>
                ) : (
                  <p className="body-small" style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
                    Ce candidat n'a pas encore d'accompagnement initialisé.
                  </p>
                )}
              </div>
            ) : (
              /* CANDIDATES LIST */
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <h2 className="heading-3">Candidats ({candidates.length})</h2>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      className="btn-secondary"
                      data-testid="sync-bookings-btn"
                      onClick={handleSyncFromBookings}
                      disabled={syncing}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', padding: '0.5rem 0.75rem' }}
                    >
                      <Users size={14} /> {syncing ? 'Synchronisation...' : 'Créer comptes depuis RDV'}
                    </button>
                    <button
                      className="btn-primary"
                      data-testid="create-candidate-btn"
                      onClick={() => setShowCreateCandidate(true)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}
                    >
                      <Plus size={16} /> Créer un candidat
                    </button>
                  </div>
                </div>

                {showCreateCandidate && (
                  <div className="card" style={{ padding: '1.5rem', marginBottom: '1.5rem', border: '2px solid #667eea' }} data-testid="create-candidate-form">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h3 style={{ fontWeight: 700, fontSize: '1rem', color: '#1e3a5f' }}>Nouveau candidat</h3>
                      <button onClick={() => setShowCreateCandidate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}><X size={18} /></button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div>
                        <label className="body-small" style={{ fontWeight: 500, display: 'block', marginBottom: '0.3rem' }}>Nom complet *</label>
                        <input type="text" value={newCandidate.name} onChange={e => setNewCandidate({...newCandidate, name: e.target.value})} placeholder="Marie Dupont" data-testid="new-candidate-name" style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }} />
                      </div>
                      <div>
                        <label className="body-small" style={{ fontWeight: 500, display: 'block', marginBottom: '0.3rem' }}>Email *</label>
                        <input type="email" value={newCandidate.email} onChange={e => setNewCandidate({...newCandidate, email: e.target.value})} placeholder="marie@email.fr" data-testid="new-candidate-email" style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }} />
                      </div>
                      <div>
                        <label className="body-small" style={{ fontWeight: 500, display: 'block', marginBottom: '0.3rem' }}>Mot de passe *</label>
                        <input type="text" value={newCandidate.password} onChange={e => setNewCandidate({...newCandidate, password: e.target.value})} placeholder="MotDePasse123!" data-testid="new-candidate-password" style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }} />
                      </div>
                      <div>
                        <label className="body-small" style={{ fontWeight: 500, display: 'block', marginBottom: '0.3rem' }}>T\u00e9l\u00e9phone</label>
                        <input type="tel" value={newCandidate.phone} onChange={e => setNewCandidate({...newCandidate, phone: e.target.value})} placeholder="06 12 34 56 78" data-testid="new-candidate-phone" style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label className="body-small" style={{ fontWeight: 500, display: 'block', marginBottom: '0.3rem' }}>Adresse postale</label>
                        <input type="text" value={newCandidate.address} onChange={e => setNewCandidate({...newCandidate, address: e.target.value})} placeholder="12 rue de la Paix, 75002 Paris" data-testid="new-candidate-address" style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }} />
                      </div>
                      <div>
                        <label className="body-small" style={{ fontWeight: 500, display: 'block', marginBottom: '0.3rem' }}>Dipl\u00f4me *</label>
                        <select value={newCandidate.programId} onChange={e => setNewCandidate({...newCandidate, programId: e.target.value})} data-testid="new-candidate-program" style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', background: 'white' }}>
                          <option value="">-- S\u00e9lectionnez --</option>
                          {programs.map(p => <option key={p.id} value={p.id}>{p.title || p.id}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="body-small" style={{ fontWeight: 500, display: 'block', marginBottom: '0.3rem' }}>Formule *</label>
                        <select value={newCandidate.formula} onChange={e => setNewCandidate({...newCandidate, formula: e.target.value})} data-testid="new-candidate-formula" style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', background: 'white' }}>
                          <option value="bronze">Bronze</option>
                          <option value="silver">Silver</option>
                          <option value="gold">Gold</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                      <button className="btn-secondary" onClick={() => setShowCreateCandidate(false)} style={{ fontSize: '0.85rem' }}>Annuler</button>
                      <button
                        className="btn-primary"
                        data-testid="submit-create-candidate"
                        disabled={creatingCandidate || !newCandidate.name || !newCandidate.email || !newCandidate.password || !newCandidate.programId}
                        onClick={async () => {
                          setCreatingCandidate(true);
                          try {
                            await adminCreateCandidate(token, newCandidate);
                            toast.success(`Candidat ${newCandidate.name} cr\u00e9\u00e9 avec succ\u00e8s !`);
                            setShowCreateCandidate(false);
                            setNewCandidate({ name: '', email: '', password: '', phone: '', address: '', programId: '', formula: 'bronze' });
                            const c = await adminGetCandidates(token);
                            setCandidates(c);
                          } catch (err) {
                            toast.error(err.message);
                          }
                          setCreatingCandidate(false);
                        }}
                        style={{ fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                      >
                        {creatingCandidate ? 'Cr\u00e9ation...' : 'Cr\u00e9er le candidat'}
                      </button>
                    </div>
                  </div>
                )}
                {(() => {
                  const renderCandidateCard = (c) => (
                  <div
                    key={c.id}
                    className="card hover-lift"
                    style={{ padding: '1rem', marginBottom: '0.75rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', cursor: 'pointer' }}
                    data-testid={`candidate-${c.id}`}
                    onClick={async () => {
                      setSelectedCandidate(c);
                      setCandidateNotes(c.adminNotes || '');
                      setCandidatePayments([]);
                      setShowPaymentForm(false);
                      try {
                        const purchases = await getUserPurchasedPrograms(c.email);
                        setCandidatePurchases(purchases);
                        const payments = await adminGetCandidatePayments(c.id, token);
                        setCandidatePayments(payments);
                        if (purchases.length > 0) {
                          const programId = purchases[0].programId || 'deap';
                          const accomp = await getAccompaniment(c.id, programId);
                          setCandidateAccompaniment(accomp);
                        }
                      } catch (e) {
                        console.error(e);
                      }
                    }}
                  >
                    <div style={{ width: 45, height: 45, borderRadius: '50%', background: (c.purchases || []).length > 0 ? '#dcfce7' : '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 700, color: (c.purchases || []).length > 0 ? '#166534' : '#92400e', fontSize: '1rem' }}>
                      {(c.name || c.email || '?')[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{c.name || '\u2014'}</div>
                      <div className="body-small" style={{ color: 'var(--text-muted)' }}>{c.email}</div>
                      {c.phone && <div className="body-small" style={{ color: '#E75480', fontWeight: 500 }}>{c.phone}</div>}
                      {c.address && <div className="body-small" style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}><MapPin size={12} /> {c.address}</div>}
                    </div>
                    {(c.purchases || []).length > 0 ? (
                      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', maxWidth: '200px' }}>
                        {(c.purchases || []).map((p, i) => (
                          <span key={i} style={{ padding: '0.15rem 0.5rem', borderRadius: '1rem', fontSize: '0.7rem', fontWeight: 600, background: p.formula === 'gold' ? '#FEF3C7' : p.formula === 'silver' ? '#F3F4F6' : p.kitId ? '#DBEAFE' : '#FDE8E8', color: p.formula === 'gold' ? '#92400E' : p.formula === 'silver' ? '#374151' : p.kitId ? '#1E40AF' : '#9B2C2C' }}>
                            {p.programId?.toUpperCase()} {p.formula ? p.formula.toUpperCase() : `Kit`}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ padding: '0.2rem 0.6rem', borderRadius: '1rem', fontSize: '0.7rem', fontWeight: 600, background: '#FEF3C7', color: '#92400E' }}>
                        {(c.targetDiploma || '').toUpperCase() || 'Prospect'}
                      </span>
                    )}
                    <div style={{ textAlign: 'right', minWidth: '90px' }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#E75480' }}>{c.bookingCount || 0} RDV</div>
                      <div className="caption" style={{ color: 'var(--text-muted)' }}>
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '\u2014'}
                      </div>
                      <button
                        data-testid={`delete-candidate-${c.id}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (window.confirm(`Supprimer ${c.name || c.email} et toutes ses données ?`)) {
                            try {
                              await deleteCandidate(c.id, token);
                              setCandidates(prev => prev.filter(x => x.id !== c.id));
                              toast.success('Candidat supprimé');
                            } catch (err) {
                              toast.error('Erreur lors de la suppression');
                            }
                          }
                        }}
                        style={{
                          marginTop: '0.35rem', padding: '0.15rem 0.4rem', borderRadius: '0.25rem',
                          fontSize: '0.6rem', fontWeight: 600, background: '#fef2f2', color: '#dc2626',
                          border: '1px solid #fecaca', cursor: 'pointer'
                        }}
                      >
                        Supprimer
                      </button>
                      <button
                        data-testid={`reset-password-${c.id}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const newPwd = window.prompt(`Nouveau mot de passe pour ${c.name || c.email} :`);
                          if (newPwd && newPwd.trim().length >= 4) {
                            try {
                              await adminResetPassword(c.id, newPwd.trim(), token);
                              toast.success(`Mot de passe de ${c.name || c.email} réinitialisé !`);
                            } catch (err) {
                              toast.error(err.message);
                            }
                          } else if (newPwd !== null) {
                            toast.error('Le mot de passe doit faire au moins 4 caractères');
                          }
                        }}
                        style={{
                          marginTop: '0.25rem', padding: '0.15rem 0.4rem', borderRadius: '0.25rem',
                          fontSize: '0.6rem', fontWeight: 600, background: '#eff6ff', color: '#1d4ed8',
                          border: '1px solid #bfdbfe', cursor: 'pointer'
                        }}
                      >
                        Changer MDP
                      </button>
                      <button
                        data-testid={`grant-access-${c.id}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const programId = window.prompt(`Attribuer un accès à ${c.name || c.email}\n\nEntrez le diplôme :\n- deeje\n- caferuis\n- deap\n- tp\n- bac-assp\n- deas\n- cap-aepe`);
                          if (!programId) return;
                          const type = window.prompt(`Type d'accès :\n\n1 - Formule complète (bronze/silver/gold)\n2 - Kit (accès à un bloc spécifique)\n\nTapez 1 ou 2`);
                          if (!type) return;
                          
                          let body = { programId: programId.trim().toLowerCase() };
                          
                          if (type.trim() === '2') {
                            const kitId = window.prompt(`ID du kit :\nExemples DEEJE: kit-4-pedagogues, kit-1-demarrer, kit-2-dc1, kit-3-dc2, kit-5-livret2, kit-6-analyse\nExemples autres: kit-bloc-1, kit-bloc-2, kit-bloc-3...`);
                            if (!kitId) return;
                            body.formula = 'kit';
                            body.kitId = kitId.trim();
                            body.kitTitle = kitId.trim();
                          } else {
                            const formula = window.prompt(`Formule :\n- bronze\n- silver (Argent)\n- gold (Or)`);
                            if (!formula) return;
                            body.formula = formula.trim().toLowerCase();
                          }
                          
                          try {
                            const result = await adminGrantAccess(c.id, body.programId, body.formula, token, body.kitId, body.kitTitle);
                            toast.success(result.message);
                            const purchases = await getUserPurchasedPrograms(c.email);
                            setCandidates(prev => prev.map(x => x.id === c.id ? {...x, purchases} : x));
                          } catch (err) {
                            toast.error(err.message);
                          }
                        }}
                        style={{
                          marginTop: '0.25rem', padding: '0.15rem 0.4rem', borderRadius: '0.25rem',
                          fontSize: '0.6rem', fontWeight: 600, background: '#f0fdf4', color: '#166534',
                          border: '1px solid #bbf7d0', cursor: 'pointer'
                        }}
                      >
                        Attribuer formule
                      </button>
                    </div>
                  </div>
                  );

                  const prospects = candidates.filter(c => !(c.purchases || []).length);
                  const withPurchases = candidates.filter(c => (c.purchases || []).length > 0);
                  const diplomaGroups = {};
                  withPurchases.forEach(c => {
                    const diploma = (c.purchases[0]?.programId || c.targetDiploma || 'autre').toUpperCase();
                    if (!diplomaGroups[diploma]) diplomaGroups[diploma] = [];
                    diplomaGroups[diploma].push(c);
                  });

                  return (
                    <>
                      {/* Section: A relancer */}
                      <div style={{ marginBottom: '2rem' }} data-testid="section-relance">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', padding: '0.5rem 0.75rem', background: '#FEF3C7', borderRadius: '8px', border: '1px solid #FCD34D' }}>
                          <span style={{ fontSize: '1.1rem' }}>&#9888;</span>
                          <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#92400E' }}>
                            {'\u00C0'} relancer ({prospects.length})
                          </h3>
                          <span style={{ fontSize: '0.75rem', color: '#92400E' }}>Prospects sans accompagnement</span>
                        </div>
                        {prospects.map(renderCandidateCard)}
                        {prospects.length === 0 && (
                          <p className="body-small" style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '1rem' }}>Aucun prospect en attente.</p>
                        )}
                      </div>

                      {/* Section: Par diplome */}
                      {Object.keys(diplomaGroups).sort().map(diploma => (
                        <div key={diploma} style={{ marginBottom: '2rem' }} data-testid={`section-diploma-${diploma.toLowerCase()}`}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', padding: '0.5rem 0.75rem', background: '#dcfce7', borderRadius: '8px', border: '1px solid #86efac' }}>
                            <GraduationCap size={18} style={{ color: '#166534' }} />
                            <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: '#166534' }}>
                              {diploma} ({diplomaGroups[diploma].length})
                            </h3>
                          </div>
                          {diplomaGroups[diploma].map(renderCandidateCard)}
                        </div>
                      ))}

                      {candidates.length === 0 && (
                        <p className="body-small" style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Aucun candidat inscrit.</p>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

  );
};

export default AdminCandidatesTab;
